(function () {
  'use strict';

  const root = document.documentElement;
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const supportsCrossDocumentTransitions = (
    typeof CSSViewTransitionRule !== 'undefined'
    && 'onpageswap' in window
    && 'onpagereveal' in window
  );
  const navigationStateKey = 'dtf-vt-navigation';
  const pageMapKey = 'dtf-vt-pages';
  const pageDepth = {
    index: 0,
    'list-collections': 1,
    search: 1,
    blog: 1,
    page: 1,
    policy: 1,
    '404': 1,
    collection: 2,
    article: 2,
    metaobject: 2,
    product: 3,
    gift_card: 3,
    cart: 4
  };
  let pendingNavigation = null;
  let pendingProductUrl = '';
  let activeChoreography = null;

  function prefersReducedMotion() {
    return reducedMotion?.matches === true;
  }

  function isDesignMode() {
    return root.dataset.designMode === 'true';
  }

  function normalizeUrl(value) {
    if (!value || (typeof value === 'string' && value.trim() === '')) return '';
    try {
      const url = new URL(value, window.location.href);
      if (url.origin !== window.location.origin) return '';
      return `${url.origin}${url.pathname}${url.search}`;
    } catch (error) {
      return '';
    }
  }

  function normalizedPath(value) {
    try {
      const path = new URL(value, window.location.origin).pathname || '/';
      return path.length > 1 ? path.replace(/\/$/, '') : path;
    } catch (error) {
      return '/';
    }
  }

  function storefrontPath(value) {
    const path = normalizedPath(value);
    const rootPath = normalizedPath(root.dataset.routeRoot || '/');
    if (rootPath === '/') return path;
    if (path === rootPath) return '/';
    if (path.startsWith(`${rootPath}/`)) return path.slice(rootPath.length) || '/';
    return path;
  }

  function readStoredJson(key, fallback) {
    try {
      const value = window.sessionStorage.getItem(key);
      return value ? JSON.parse(value) : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function writeStoredJson(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage can be unavailable in privacy modes; navigation still works normally.
    }
  }

  function removeStoredValue(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // No cleanup is needed when storage is unavailable.
    }
  }

  function rememberPage(url, type) {
    const normalized = normalizeUrl(url);
    if (!normalized || !type) return;
    const pages = readStoredJson(pageMapKey, {});
    pages[normalized] = type;
    const urls = Object.keys(pages);
    while (urls.length > 24) delete pages[urls.shift()];
    writeStoredJson(pageMapKey, pages);
  }

  function rememberedPage(url) {
    const normalized = normalizeUrl(url);
    if (!normalized) return '';
    return readStoredJson(pageMapKey, {})[normalized] || '';
  }

  function classifyPage(url) {
    const remembered = rememberedPage(url);
    if (remembered) return remembered;

    const path = storefrontPath(url);
    const cartPath = storefrontPath(root.dataset.routeCart || '/cart');
    const searchPath = storefrontPath(root.dataset.routeSearch || '/search');
    const accountPath = storefrontPath(root.dataset.routeAccount || '/account');

    if (path === '/') return 'index';
    if (path === cartPath) return 'cart';
    if (path === searchPath) return 'search';
    if (path === '/password') return 'password';
    if (path === '/challenge') return 'captcha';
    if (path === accountPath || path.startsWith(`${accountPath}/`)) return 'customers/account';
    if (path === '/collections') return 'list-collections';
    if (path.startsWith('/collections/')) return 'collection';
    if (path.startsWith('/products/')) return 'product';
    if (path.startsWith('/pages/')) return 'page';
    if (path.startsWith('/policies/')) return 'policy';
    if (path.startsWith('/metaobjects/')) return 'metaobject';
    if (path.startsWith('/gift_cards/')) return 'gift_card';
    if (path.startsWith('/blogs/')) {
      const segments = path.split('/').filter(Boolean);
      return segments.length > 2 && segments[2] !== 'tagged' ? 'article' : 'blog';
    }
    return 'page';
  }

  function isExcludedNavigation(url, type) {
    const path = storefrontPath(url);
    return (
      !url
      || type === 'password'
      || type === 'captcha'
      || type.startsWith('customers/')
      || /^\/(?:checkout|checkouts|customer_authentication)(?:\/|$)/.test(path)
      || /^\/(?:apps|a|community|tools)(?:\/|$)/.test(path)
    );
  }

  function transitionFlow(fromType, toType, activation) {
    const fromIndex = activation?.from?.index;
    const toIndex = activation?.entry?.index;
    if (
      activation?.navigationType === 'traverse'
      && Number.isInteger(fromIndex)
      && Number.isInteger(toIndex)
      && fromIndex >= 0
      && toIndex >= 0
      && fromIndex !== toIndex
    ) {
      return toIndex < fromIndex ? 'backward' : 'forward';
    }

    const fromDepth = pageDepth[fromType] ?? 1;
    const toDepth = pageDepth[toType] ?? 1;
    return toDepth < fromDepth ? 'backward' : 'forward';
  }

  function safeSkip(viewTransition) {
    try {
      viewTransition?.skipTransition?.();
    } catch (error) {
      // The transition may already be finished or skipped.
    }
  }

  function bindInteractionCancellation(viewTransition, onCancel) {
    let listening = true;
    const cleanup = () => {
      if (!listening) return;
      listening = false;
      window.removeEventListener('pointerdown', cancel);
      window.removeEventListener('keydown', cancel);
    };
    const cancel = () => {
      safeSkip(viewTransition);
      onCancel?.();
      cleanup();
    };

    window.addEventListener('pointerdown', cancel, { once: true });
    window.addEventListener('keydown', cancel, { once: true });
    return cleanup;
  }

  function isInFirstViewport(element) {
    if (!element?.isConnected || element.hidden) return false;
    const bounds = element.getBoundingClientRect();
    return (
      bounds.width > 0
      && bounds.height > 0
      && bounds.right > 0
      && bounds.left < window.innerWidth
      && bounds.bottom > 0
      && bounds.top < window.innerHeight
    );
  }

  function isContainedInFirstViewport(element) {
    if (!isInFirstViewport(element)) return false;
    const bounds = element.getBoundingClientRect();
    return bounds.top >= 0 && bounds.bottom <= window.innerHeight;
  }

  function startEntryChoreography() {
    const main = document.querySelector('#MainContent');
    if (!main) return { cancel() {}, finished: Promise.resolve() };

    let sections = [...main.querySelectorAll(':scope > .shopify-section, :scope > [id^="shopify-section-"]')];
    if (sections.length === 0) sections = [...main.children];
    sections = sections.filter(isContainedInFirstViewport).slice(0, 5);

    const cards = [...main.querySelectorAll([
      '.prod',
      '.bcard',
      '.cstack',
      '.dcard',
      '.fan-card',
      '.wcard',
      '.rstack',
      '.rcard',
      '.pcard',
      '.hcard',
      '.promo__banner'
    ].join(','))].filter(isInFirstViewport).slice(0, 10);
    const elements = [...new Set([...sections, ...cards])];
    if (elements.length === 0) return { cancel() {}, finished: Promise.resolve() };

    let timer = 0;
    let resolveFinished;
    let active = true;
    const finished = new Promise(resolve => {
      resolveFinished = resolve;
    });
    const cleanup = () => {
      if (!active) return;
      active = false;
      window.clearTimeout(timer);
      delete root.dataset.vtChoreography;
      sections.forEach(section => {
        section.removeAttribute('data-vt-enter-section');
        section.style.removeProperty('--vt-enter-order');
      });
      cards.forEach(card => {
        card.removeAttribute('data-vt-enter-card');
        card.style.removeProperty('--vt-enter-order');
      });
      resolveFinished();
    };

    sections.forEach((section, index) => {
      section.setAttribute('data-vt-enter-section', '');
      section.style.setProperty('--vt-enter-order', String(index));
    });
    cards.forEach((card, index) => {
      card.setAttribute('data-vt-enter-card', '');
      card.style.setProperty('--vt-enter-order', String(index));
    });
    root.dataset.vtChoreography = '';
    timer = window.setTimeout(cleanup, 880);
    return { cancel: cleanup, finished };
  }

  function clearTransitionAttributes() {
    delete root.dataset.vtFlow;
    delete root.dataset.vtProductMorph;
  }

  function markProductNavigation(value) {
    const url = normalizeUrl(value);
    if (!url) return;
    pendingProductUrl = url;
    root.dataset.vtProductMorph = '';
  }

  function resetProductNavigation() {
    pendingProductUrl = '';
    delete root.dataset.vtProductMorph;
  }

  function activateProductTransitionTarget() {
    const target = document.querySelector('[data-product-transition-target]');
    document.querySelectorAll('[data-product-transition-target-active]').forEach(element => {
      element.removeAttribute('data-product-transition-target-active');
    });
    target?.setAttribute('data-product-transition-target-active', '');
  }

  /* F22b universal page entrance. Liquid renders html[data-page-enter="run"] outside the
     theme editor and its visual section preview, and theme.css animates the first viewport
     from that attribute alone. This controller only narrows it: hold while prerendering
     (activation settles to done, so a prerendered page never replays or flashes), vt while
     a cross-document view transition owns the entrance, done once the entrance has settled. */
  const pageEnterAnimationNames = new Set(['page-enter-fade', 'page-enter-rise', 'page-enter-rise-sm']);
  const pageEnterCapMs = 1200;
  const pageEnterHardCapMs = 4000;
  let pageEnterWatching = false;

  function pageEnterState() {
    return root.getAttribute('data-page-enter') || '';
  }

  function settlePageEnter() {
    const state = pageEnterState();
    if (state && state !== 'done') root.setAttribute('data-page-enter', 'done');
  }

  function watchPageEnter() {
    if (pageEnterWatching || pageEnterState() !== 'run') return;
    pageEnterWatching = true;

    let capTimer = 0;
    let hardCapTimer = 0;
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(capTimer);
      window.clearTimeout(hardCapTimer);
      window.removeEventListener('pointerdown', settle, true);
      window.removeEventListener('keydown', settle, true);
      if (pageEnterState() === 'run') settlePageEnter();
    };
    window.addEventListener('pointerdown', settle, true);
    window.addEventListener('keydown', settle, true);

    const collect = () => {
      window.requestAnimationFrame(() => {
        if (settled) return;
        if (pageEnterState() !== 'run') {
          settle();
          return;
        }
        hardCapTimer = window.setTimeout(settle, pageEnterHardCapMs);
        if (typeof document.getAnimations !== 'function') {
          capTimer = window.setTimeout(settle, pageEnterCapMs);
          return;
        }
        const animations = document.getAnimations().filter(animation => (
          pageEnterAnimationNames.has(animation.animationName)
        ));
        animations.forEach(animation => {
          const target = animation.effect?.target;
          if (!(target instanceof Element) || target.getBoundingClientRect().top < window.innerHeight) return;
          try {
            animation.finish();
          } catch (error) {
            // A cancelled or already finished animation needs no work.
          }
        });
        // The cap counts from the first painted frame of the entrance, not from
        // DOMContentLoaded, so a late first frame is never cut short mid-fade.
        Promise.allSettled(animations.map(animation => animation.ready)).then(() => {
          if (!settled) capTimer = window.setTimeout(settle, pageEnterCapMs);
        });
        Promise.allSettled(animations.map(animation => animation.finished)).then(settle);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', collect, { once: true });
    } else {
      collect();
    }
  }

  function initializePageEnter() {
    if (!root.hasAttribute('data-page-enter')) return;
    if (isDesignMode()) {
      root.removeAttribute('data-page-enter');
      return;
    }
    if (document.prerendering === true) {
      root.setAttribute('data-page-enter', 'hold');
      document.addEventListener('prerenderingchange', () => {
        if (pageEnterState() === 'hold') settlePageEnter();
      }, { once: true });
      return;
    }
    watchPageEnter();
  }

  window.addEventListener('pageswap', settlePageEnter);
  window.addEventListener('pagehide', settlePageEnter);
  window.addEventListener('pageshow', event => {
    if (event.persisted) settlePageEnter();
  });
  initializePageEnter();

  if (!supportsCrossDocumentTransitions) return;

  window.DTFViewTransitions = {
    canUseCrossDocument() {
      return supportsCrossDocumentTransitions && !prefersReducedMotion() && !isDesignMode();
    },
    markProductNavigation,
    resetProductNavigation,
    normalizeUrl
  };

  rememberPage(window.location.href, root.dataset.pageType || 'page');

  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (
      !link
      || event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
      || link.hasAttribute('download')
      || (link.target && link.target !== '_self')
    ) return;

    const url = normalizeUrl(link.href);
    if (!url) return;
    const type = classifyPage(url);
    const navigation = { url, type, excluded: isExcludedNavigation(url, type) };
    pendingNavigation = navigation;
    window.queueMicrotask(() => {
      if (event.defaultPrevented && pendingNavigation === navigation) {
        pendingNavigation = null;
        resetProductNavigation();
      }
    });
  }, { capture: true });

  document.addEventListener('submit', event => {
    const form = event.target.closest?.('form');
    const url = normalizeUrl(form?.action || window.location.href);
    const navigation = { url, type: classifyPage(url), excluded: true };
    pendingNavigation = navigation;
    window.queueMicrotask(() => {
      if (event.defaultPrevented && pendingNavigation === navigation) pendingNavigation = null;
    });
  }, { capture: true });

  window.addEventListener('pageswap', event => {
    const viewTransition = event.viewTransition;
    if (!viewTransition) return;

    activeChoreography?.cancel();
    activeChoreography = null;

    const destinationUrl = normalizeUrl(event.activation?.entry?.url || pendingNavigation?.url || '');
    const pendingMatchesDestination = pendingNavigation?.url === destinationUrl;
    const destinationType = (
      pendingMatchesDestination && pendingNavigation.type
        ? pendingNavigation.type
        : classifyPage(destinationUrl)
    );
    const fromType = root.dataset.pageType || 'page';
    const excluded = (
      (pendingMatchesDestination && pendingNavigation.excluded === true)
      || isExcludedNavigation(window.location.href, fromType)
      || isExcludedNavigation(destinationUrl, destinationType)
    );
    if (prefersReducedMotion() || isDesignMode() || excluded) {
      removeStoredValue(navigationStateKey);
      clearTransitionAttributes();
      safeSkip(viewTransition);
      return;
    }

    const flow = transitionFlow(fromType, destinationType, event.activation);
    const productMorph = pendingProductUrl !== '' && pendingProductUrl === destinationUrl && destinationType === 'product';
    root.dataset.vtFlow = flow;
    if (productMorph) root.dataset.vtProductMorph = '';
    else delete root.dataset.vtProductMorph;
    writeStoredJson(navigationStateKey, {
      fromType,
      toType: destinationType,
      toUrl: destinationUrl,
      flow,
      productMorph
    });

    const cleanupCancellation = bindInteractionCancellation(viewTransition);
    Promise.resolve(viewTransition.finished).catch(() => {}).then(() => {
      cleanupCancellation();
      pendingNavigation = null;
      resetProductNavigation();
      delete root.dataset.vtFlow;
      document.querySelectorAll('[data-product-transition-source-active]').forEach(source => {
        source.removeAttribute('data-product-transition-source-active');
      });
    });
  });

  window.addEventListener('pagereveal', event => {
    const viewTransition = event.viewTransition;
    if (!viewTransition) {
      removeStoredValue(navigationStateKey);
      clearTransitionAttributes();
      return;
    }
    if (prefersReducedMotion() || isDesignMode()) {
      removeStoredValue(navigationStateKey);
      clearTransitionAttributes();
      safeSkip(viewTransition);
      return;
    }

    const currentUrl = normalizeUrl(window.location.href);
    const currentType = root.dataset.pageType || 'page';
    if (isExcludedNavigation(currentUrl, currentType)) {
      removeStoredValue(navigationStateKey);
      clearTransitionAttributes();
      safeSkip(viewTransition);
      return;
    }
    const storedState = readStoredJson(navigationStateKey, null);
    const activation = window.navigation?.activation;
    const fromType = storedState?.toUrl === currentUrl
      ? storedState.fromType
      : classifyPage(activation?.from?.url || '');
    const flow = storedState?.toUrl === currentUrl
      ? storedState.flow
      : transitionFlow(fromType, currentType, activation);
    const productMorph = storedState?.toUrl === currentUrl && storedState.productMorph === true;
    const pageEnter = pageEnterState();
    if (pageEnter === 'run' || pageEnter === 'hold') root.setAttribute('data-page-enter', 'vt');
    root.dataset.vtFlow = flow;
    if (productMorph) {
      root.dataset.vtProductMorph = '';
      activateProductTransitionTarget();
    } else {
      delete root.dataset.vtProductMorph;
    }

    let cancelled = false;
    let choreography = { cancel() {}, finished: Promise.resolve() };
    const cleanupCancellation = bindInteractionCancellation(viewTransition, () => {
      cancelled = true;
      choreography.cancel();
    });
    const choreographyFinished = Promise.resolve(viewTransition.ready)
      .then(() => {
        if (cancelled) return;
        choreography = startEntryChoreography();
        activeChoreography = choreography;
        return choreography.finished;
      })
      .catch(() => {});

    Promise.allSettled([viewTransition.finished, choreographyFinished]).then(() => {
      cleanupCancellation();
      if (activeChoreography === choreography) activeChoreography = null;
      removeStoredValue(navigationStateKey);
      clearTransitionAttributes();
      if (pageEnterState() === 'vt') settlePageEnter();
    });
  });

  window.addEventListener('pageshow', event => {
    pendingNavigation = null;
    if (event.persisted || !root.dataset.vtFlow) resetProductNavigation();
    if (event.persisted) {
      removeStoredValue(navigationStateKey);
      delete root.dataset.vtFlow;
    }
  });
})();
